import React from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import SegmentedControl from '@tencent/dui/lib/components/SegmentedControl';
import Select from '@tencent/dui/lib/components/Select';
import i18n from '@i18next/i18n';
import { IReactionDisposer, reaction } from 'mobx';
import { ISlideSizeConfigDialogProps } from '@src/slide/application/service/slide-size-service/index.interface';
import Styles from './slide-size-config-dialog.less';
import { NumberInput } from '@tencent/wb-ext-form';
import { STSlideSizeType } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import {
    DEFAULT_4_3_WIDTH,
    DEFAULT_16_9_WIDTH,
    DEFAULT_4_3_HEIGHT,
    DEFAULT_16_9_HEIGHT,
} from '@src/slide/application/service/slide-size-service/core/constants';
import {
    convertPxToCm,
    convertCmToPx,
} from '@src/slide/infrastructure/module/independent/unit';
import { ScaleConfirmDialog } from '@src/slide/application/service/slide-size-service/core/scale-confirm-dialog';

export interface ISlideSizeConfigState {
    visible: boolean;
    slideSizeType: STSlideSizeType;
    slideWidth: number;
    slideHeight: number;
    direction: E_DIRECTION;
}

const NUMBER_RANGE = {
    MIN: 3,
    MAX: 130,
    STEP: 0.1,
};

enum E_SCALE_TYPE {
    enlarge,
    shrink,
    enlargeOrShrink,
}

enum E_DIRECTION {
    horz = '1',
    vert = '2',
}

export class SlideSizeConfigDialog extends React.PureComponent<ISlideSizeConfigDialogProps, ISlideSizeConfigState> {
    private reaction: IReactionDisposer;
    public constructor(props: ISlideSizeConfigDialogProps) {
        super(props);
        const { slideWidth, slideHeight, type } = this.props.uiDataService.presentation.props;
        this.state = {
            visible: true,
            slideSizeType: type || STSlideSizeType.screen16x9,
            slideWidth: convertPxToCm(slideWidth),
            slideHeight: convertPxToCm(slideHeight),
            direction: slideWidth >= slideHeight ? E_DIRECTION.horz : E_DIRECTION.vert,
        };
        this.initReaction();
    }

    public componentWillUnmount() {
        this.reaction?.();
    }

    public render() {
        return <Modal
            visible={this.props.slideSizeService.dialogVisible}
            onCancel={() => {
                this.props.slideSizeService.hideDialog();
            }}
            onOk={this.handleOk}
            title={i18n.t('自定义比例')}
        >
            <div className={Styles['slide-size-config-dialog-content']}>
                <div className={Styles['slide-size-config-dialog-content-row']}>
                    <div className={Styles['slide-size-config-dialog-content-row-label']}>
                        { i18n.t('幻灯片大小') }
                    </div>
                    <div className={Styles['slide-size-config-dialog-content-row-form']}>
                        <Select
                            onChange={this.handleScaleChange}
                            value={this.state.slideSizeType}
                            style={{
                                width: 240,
                                height: 36,
                            }}
                        >
                            <Select.Option value={STSlideSizeType.screen4x3}>
                                {
                                    i18n.t('标准（4：3）')
                                }
                            </Select.Option>
                            <Select.Option value={STSlideSizeType.screen16x9} >
                                {
                                    i18n.t('宽屏（16：9）')
                                }
                            </Select.Option>
                            <Select.Option value={STSlideSizeType.custom}>
                                {
                                    i18n.t('自定义')
                                }</Select.Option>
                        </Select>
                    </div>
                </div>
                <div className={Styles['slide-size-config-dialog-content-row']}>
                    <div className={Styles['slide-size-config-dialog-content-row-label']}>
                        { i18n.t('宽度') }
                    </div>
                    <div className={Styles['slide-size-config-dialog-content-row-form']}>
                        <NumberInput
                            min={NUMBER_RANGE.MIN}
                            max={NUMBER_RANGE.MAX}
                            allowDecimal={true}
                            toFixed={2}
                            value={this.state.slideWidth.toString()}
                            step={NUMBER_RANGE.STEP}
                            suffix={i18n.t('厘米')}
                            style={{ width: 140 }}
                            onChange={this.handleWidthChange}>
                        </NumberInput>
                    </div>
                </div>
                <div className={Styles['slide-size-config-dialog-content-row']}>
                    <div className={Styles['slide-size-config-dialog-content-row-label']}>
                        { i18n.t('高度') }
                    </div>
                    <div className={Styles['slide-size-config-dialog-content-row-form']}>
                        <NumberInput
                            min={NUMBER_RANGE.MIN}
                            max={NUMBER_RANGE.MAX}
                            allowDecimal={true}
                            toFixed={2}
                            value={this.state.slideHeight.toString()}
                            step={NUMBER_RANGE.STEP}
                            suffix={i18n.t('厘米')}
                            style={{ width: 140 }}
                            onChange={this.handleHeightChange}>
                        </NumberInput>
                    </div>
                </div>
                <div className={Styles['slide-size-config-dialog-content-row']} style={{
                    marginBottom: 0,
                }}>
                    <div className={Styles['slide-size-config-dialog-content-row-label']}>
                        { i18n.t('幻灯片方向') }
                    </div>
                    <div className={Styles['slide-size-config-dialog-content-row-form']}>
                        <SegmentedControl
                            style={{
                                margin: '4px 0',
                                width: 160,
                            }}
                            onClick={this.handleDirectionClick}
                            activeId={this.state.direction}>
                            <SegmentedControl.Segment
                                id={E_DIRECTION.horz}>
                                { i18n.t('横向') }
                            </SegmentedControl.Segment>
                            <SegmentedControl.Segment
                                id={E_DIRECTION.vert}>
                                { i18n.t('纵向') }
                            </SegmentedControl.Segment>
                        </SegmentedControl>
                    </div>
                </div>
            </div>
            <ScaleConfirmDialog
                uiDataService={this.props.uiDataService}
                slideSizeService={this.props.slideSizeService}
                slideWidth={this.state.slideWidth}
                slideHeight={this.state.slideHeight}
                slideSizeType={this.state.slideSizeType}
            />
        </Modal>;
    }

    private handleDirectionClick = (e: React.MouseEvent, direction: E_DIRECTION) => {
        const max = Math.max(this.state.slideWidth, this.state.slideHeight);
        const min = Math.min(this.state.slideWidth, this.state.slideHeight);
        let slideWidth;
        let slideHeight;
        if (direction === E_DIRECTION.horz) {
            slideWidth = max;
            slideHeight = min;
        } else {
            slideWidth = min;
            slideHeight = max;
        }
        this.setState({
            direction,
            slideWidth,
            slideHeight,
        });
    };

    private handleSizeChangeOnDirection = (slideWidth: number, slideHeight: number) => {
        if (slideWidth >= slideHeight) {
            this.setState({
                direction: E_DIRECTION.horz,
            });
        } else {
            this.setState({
                direction: E_DIRECTION.vert,
            });
        }
    };

    private handleWidthChange = (width: string) => {
        if (width === '') width = '0';
        let slideWidth = Number(Number(width.replace(i18n.t('厘米'), '')).toFixed(2));
        if (slideWidth > NUMBER_RANGE.MAX) slideWidth = NUMBER_RANGE.MAX;
        if (slideWidth < NUMBER_RANGE.MIN) slideWidth = NUMBER_RANGE.MIN;
        this.handleSizeChangeOnDirection(slideWidth, this.state.slideHeight);
        this.setState({
            slideWidth,
            slideSizeType: STSlideSizeType.custom,
        });
    };

    private handleHeightChange = (height: string) => {
        if (height === '') height = '0';
        let slideHeight = Number(Number(height.replace(i18n.t('厘米'), '')).toFixed(2));
        if (slideHeight > NUMBER_RANGE.MAX) slideHeight = NUMBER_RANGE.MAX;
        if (slideHeight < NUMBER_RANGE.MIN) slideHeight = NUMBER_RANGE.MIN;
        this.handleSizeChangeOnDirection(this.state.slideWidth, slideHeight);
        this.setState({
            slideHeight,
            slideSizeType: STSlideSizeType.custom,
        });
    };

    private handleScaleChange = (type: STSlideSizeType) => {
        switch (type) {
            case STSlideSizeType.screen4x3:
                this.setState({
                    slideWidth: DEFAULT_4_3_WIDTH,
                    slideHeight: DEFAULT_4_3_HEIGHT,
                    slideSizeType: STSlideSizeType.screen4x3,
                    direction: E_DIRECTION.horz,
                });
                break;
            case STSlideSizeType.screen16x9:
                this.setState({
                    slideWidth: DEFAULT_16_9_WIDTH,
                    slideHeight: DEFAULT_16_9_HEIGHT,
                    slideSizeType: STSlideSizeType.screen16x9,
                    direction: E_DIRECTION.horz,
                });
                break;
            case STSlideSizeType.custom:
                this.setState({
                    slideSizeType: STSlideSizeType.custom,
                });
                break;
        }
    };

    private handleOk = (event) => {
        // 根据 dui 文档，没有 event 参数表示是 enter 按键触发。
        // 按键触发保存时由于不会触发 input 的 blur，也就无法触发 input 值的合法化校验，导致非法参数被保存，所以在这里禁用 enter 保存
        if (!event) {
            return;
        }
        const { slideWidth, slideHeight } = this.props.uiDataService.presentation.props;
        const newSlideWidth = Math.round(convertCmToPx(this.state.slideWidth));
        const newSlideHeight = Math.round(convertCmToPx(this.state.slideHeight));
        if (newSlideWidth === Math.round(slideWidth) && newSlideHeight === Math.round(slideHeight)) {
            this.props.slideSizeService.hideDialog();
            return;
        }

        this.props.slideSizeService.confirmVisible = true;
    };

    private initReaction = () => {
        this.reaction = reaction(() => this.props.slideSizeService.dialogVisible, (visible) => {
            this.setState({
                visible,
            });
        });
    };
}
